import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AtlasManageService } from '@app/auth/atlas-manage/atlas-manage.service';
import { fromEvent, Subscription } from 'rxjs';
import { isNil } from 'ng-zorro-antd/core/util';
import { debounceTime } from 'rxjs/operators';
import { Ng2UmeditorService } from '@shared/ng2-umeditor/ng2-umeditor.service';
import { RxjsSubscription } from '@common';
import * as echarts from 'echarts';
import { EchartsNode } from '@shared/model/course.models';
import icons from '../echarts-icon.json';
import { NzMessageService } from 'ng-zorro-antd/message';

declare let G6: any;

/**
 * 校本图谱展示
 */
@Component({
  selector: 'fl-atlas-school',
  templateUrl: './atlas-school.component.html',
  styleUrls: ['./atlas-school.component.scss'],
  standalone: false,
})
export class AtlasSchoolComponent implements OnInit, OnDestroy {
  private atlasSrv = inject(AtlasManageService);
  private ng2UmeditorService = inject(Ng2UmeditorService);
  private msgSrv = inject(NzMessageService);

  graph;

  subs: RxjsSubscription = new RxjsSubscription();

  option: echarts.EChartsOption = {};

  ngOnInit(): void {
    this.subs.subscriptions = this.atlasSrv.nodes.subscribe(node => {
      if (!isNil(node)) {
        if (this.graph) {
          this.graph.dispose();
          this.graph = null;
        }
        this.initEcharts(node);
        this.graphEvent();
        // this.ng2UmeditorService.loadScript(
        //   'https://gw.alipayobjects.com/os/lib/antv/g6/4.8.15/dist/g6.min.js',
        //   () => {
        //     this.registerCustomNode();
        //     this.initChart(node);
        //   }
        // );
      }
    });
    this.subs.subscriptions = this.atlasSrv.updateNodes.subscribe(node => {
      if (!isNil(node)) {
        this.initEcharts(node);
      }
    });
    this.subs.subscriptions = fromEvent(window, 'resize')
      .pipe(debounceTime(800))
      .subscribe(e => {
        if (this.graph) this.graph.resize();
      });
    this.subs.subscriptions = this.atlasSrv.changeLevel$.subscribe(data => {
      if (!isNil(data)) {
        const { nodes, level } = data;
        this.initEcharts(nodes, level);
      }
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    if (this.graph) {
      this.graph.dispose();
      this.graph = null;
    }
    // const src = document.getElementsByTagName('script');
    // const el = src[src.length - 1];
    // el.parentNode.removeChild(el);
  }

  registerCustomNode() {
    if (!G6) return;
    const text_offset = 18;
    const visible_stroke = '#6B94F8';
    const visible_fill = '#F0F4FE';

    const disable_stoke = '#A8A8A8';
    // 注册自定义节点的样式
    G6.registerNode('forbidden-node', {
      draw(cfg, group) {
        const { backgroundConfig: backgroundStyle, style, labelCfg: labelStyle } = cfg;

        let keyShape;
        if (cfg.isVisible) {
          keyShape = group.addShape('circle', {
            attrs: {
              x: 0,
              y: 0,
              r: 10,
              fill: visible_fill,
              stroke: visible_stroke,
            },
            name: 'text-circle',
          });
        } else {
          keyShape = group.addShape('text', {
            attrs: {
              x: 0,
              y: 0,
              fontFamily: 'icon', // 对应css里面的font-family: "iconfont";
              textAlign: 'center',
              textBaseline: 'middle',
              text: '\ue842',
              fontSize: 20,
              stroke: disable_stoke,
              fill: disable_stoke,
            },
            name: 'text-circle',
          });
        }

        const iconBbox = keyShape.getBBox();
        const text = group.addShape('text', {
          attrs: {
            x: cfg.children && cfg.children.length > 0 ? text_offset * -1 : text_offset,
            y: 6,
            textAlign: cfg.children && cfg.children.length > 0 ? 'right' : 'left',
            text: cfg.label,
            ...labelStyle.style,
          },
          name: 'text',
        });

        // 获取到添加的label的宽度
        const bbox = text.getBBox();
        // 在根节点时添加对应 icon
        if (!cfg.children || cfg.children?.length === 0) {
          if (cfg.chapterNodes?.length > 0) {
            group.addShape('circle', {
              attrs: {
                x: bbox.width + iconBbox.width + text_offset,
                y: 0,
                r: 10,
                fill: '#9FD476',
              },
              name: 'text-shape1',
            });
            group.addShape('text', {
              attrs: {
                x: bbox.width + iconBbox.width + text_offset - 1,
                y: 6,
                textAlign: 'center',
                text: cfg.chapterNodes.length,
                ...labelStyle.style,
              },
              name: 'text-shape1',
            });
          } else {
            group.addShape('text', {
              attrs: {
                x: bbox.width + iconBbox.width + text_offset,
                y: 0,
                fontFamily: 'icon', // 对应css里面的font-family: "iconfont";
                textAlign: 'center',
                textBaseline: 'middle',
                text: '\ue70f',
                fontSize: 18,
                fill: '#EA0000',
              },
              name: 'text-shape1',
            });
          }
        }

        return keyShape;
      },
    });
  }

  formatData(graph: EchartsNode) {
    graph.name = graph.title;
    graph.value = graph.key;
    if (graph.children) {
      graph.children.forEach(_graph => this.formatData(_graph));
    }
  }

  /**
   * 初始化 echarts 图谱
   * @param nodes
   */
  initEcharts(nodes, level: number = 2) {
    this.formatData(nodes);
    if (!this.graph) {
      const container: HTMLElement = document.getElementById('chart');
      this.graph = echarts.init(container);
    }
    this.graph.setOption(
      (this.option = {
        tooltip: {
          show: false,
        },
        series: [
          {
            type: 'tree',
            data: [nodes],
            top: '1%',
            left: '7%',
            bottom: '1%',
            right: '20%',
            symbolSize: 12,
            symbol: (value, { data }) => {
              // @ts-ignore
              return data?.isVisible ? 'emptyCircle' : `image://${icons.disableIcon}`;
            },
            label: {
              position: 'left',
              verticalAlign: 'middle',
              align: 'right',
              fontSize: 12,
            },
            leaves: {
              label: {
                position: 'right',
                verticalAlign: 'middle',
                align: 'left',
              },
            },
            roam: true,
            expandAndCollapse: true,
            animationDuration: 550,
            animationDurationUpdate: 750,
            initialTreeDepth: level,
            animation: true,
            /**
             * tree 数据过多会关闭动画，导致展开收起后，连接线不会消失。
             * 强制开启动画
             */
            animationThreshold: 2000000,
          },
        ],
        label: {
          formatter: ({ data }) => {
            let str = `${data.name} `;
            const noNodes = `{noNodes|} `;
            const nodes = `{nodes|${data.chapterNodes?.length}}`;
            if (data.chapterNodes && data.isLeaf) {
              if (data.chapterNodes?.length > 0) {
                str += nodes;
              } else {
                /**
                 * 2.0图谱不显示，未关联题库知识点标识
                 */
                if (!this.atlasSrv.isStartV2) str += noNodes;
              }
            }
            return str;
          },
          rich: {
            nodes: {
              borderRadius: 12,
              backgroundColor: '#9FD476',
              width: 16,
              height: 16,
              align: 'center',
              lineHeight: 12,
            },
            noNodes: {
              backgroundColor: {
                image: icons.warn,
              },
            },
          },
        },
      }),
      {
        notMerge: true,
      }
    );
  }

  graphEvent() {
    this.graph.on('mousedown', params => {
      if (params.event.target.culling === false) {
        // let nodeIndex = params.dataIndex;
        // this.graph._chartsViews[0]._data.tree._nodes[nodeIndex].isExpand =
        //   !this.graph._chartsViews[0]._data.tree._nodes[nodeIndex].isExpand;
      }
    });
    // 监听点击事件
    this.graph.on('click', params => {
      if (params.componentType === 'series') {
        /**
         * 点击节点
         */
        if (params.event.target.culling === true) {
          // params.data.collapsed = !isNil(params.data.collapsed) ? !params.data.collapsed : false;
          if (params.data.collapsed) {
            this.atlasSrv.expendNodeSet.delete(params.data.id);
          } else {
            this.atlasSrv.expendNodeSet.add(params.data.id);
          }
          this.atlasSrv.expandNode$.next(params.data);
        } else if (params.event.target.culling === false) {
          this.atlasSrv.clickNodes.next(params.data);
        }
      }
    });
  }

  /**
   * 初始化图谱
   */
  initChart(nodes) {
    // {
    //   type: 'collapse-expand',
    //     onChange: function onChange(item, collapsed) {
    //   const data = item.getModel();
    //   data.collapsed = collapsed;
    //   return true;
    // },
    // },
    const container: HTMLElement = document.getElementById('chart');
    const width = container.scrollWidth;
    const height = container.scrollHeight || 500;
    this.graph = new G6.TreeGraph({
      container: 'chart',
      modes: {
        default: ['drag-canvas', 'zoom-canvas', 'activate-relations'],
      },
      defaultEdge: {
        type: 'cubic-horizontal',
        style: {
          stroke: '#aaa',
          lineAppendWidth: 2,
          opacity: 0.6,
        },
      },
      defaultNode: {
        type: 'forbidden-node',
        labelCfg: {
          style: {
            fill: '#333',
            fontSize: 12,
            offset: 10,
          },
        },
      },
      edgeStateStyles: {
        inactive: {
          stroke: '#999',
        },
      },
      layout: {
        type: 'compactBox',
        direction: 'LR',
        getHeight: function getHeight() {
          return 16;
        },
        getWidth: function getWidth() {
          return 16;
        },
        getVGap: function getVGap() {
          return 10;
        },
        getHGap: function getHGap() {
          return 100;
        },
      },
    });

    this.graph.node(nodeItem => {
      return {
        label: nodeItem.title,
        labelCfg: {
          offset: 10,
          position: nodeItem.children && nodeItem.children.length > 0 ? 'left' : 'right',
        },
      };
    });

    this.graph.on('node:click', evt => {
      // 点击 label 标题
      const data = evt.item._cfg.model;
      this.atlasSrv.clickNodes.next(data);
      // if (evt.target.cfg.name === 'text-circle' || evt.target.cfg.name === 'text') {
      //   setTimeout(() => {
      //     this.graph.focusItem(evt.item._cfg.id, true);
      //   }, 800);
      // }
    });

    this.graph.data(nodes);
    this.graph.render();

    const point = this.graph.getGraphCenterPoint();
    this.graph.focusItem(nodes.children[0].children[0].id, true);
    this.graph.translate(-200, -100);

    // this.graph.fitView();
  }
}
